import React, { useContext, useEffect, useMemo, useState } from "react";
import Header from "components/common/Header/Header";
import SideBarNew from "components/common/SideBar/SideBarNew";
import { LoginContext } from "helpers/LoginContext";
import { useDispatch, useSelector } from "react-redux";
import { stripePricingTableListRequest } from "store/actions/sp.actions";
import {
  getStripePricingList,
  getStripePricingTableLoader,
  getDashboard,
} from "store/selector/sp.selector";
import { getSPDashboardRequest } from "store/actions/sp.actions";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { getPaymentSettings } from "store/selector/auth.selector";
import api from "store/services/sp.services";
import { classNames } from "Utils/utils";
import CustomButton from "components/common/Button/CustomButton";
import { BiExit } from "react-icons/bi";
import { callLogout } from "store/actions/auth.actions";
import { useNavigate } from "react-router";
import PricingTableNew from "./NewPricingTable/PricingTableNew";
import usePayrollAuthentication from "hooks/usePayrollAuthentication";

const PricingTable = ({ istrailExpired = false }) => {
  const { sideBarPadding, unSubscribeChannels, isTrialChoosePlanBtnClick } =
    useContext(LoginContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
   
 const {
    isAuthenticateForPayrolls} = usePayrollAuthentication();
  const pricingList = useSelector(getStripePricingList);
  
  const pricingListLoader = useSelector(getStripePricingTableLoader);
  const paymentSettings = useSelector(getPaymentSettings);
  const spDashboard = useSelector(getDashboard);


const isShowXeroButton = useMemo(() => {
  return paymentSettings?.subscription_status === false &&
    paymentSettings?.subscription_id === null &&
    paymentSettings?.xero_subscription_status !== "CANCELLED" &&
    paymentSettings?.xero_subscription_status !== "PAST_DUE" &&
    isAuthenticateForPayrolls?.xero
    ? true
    : false;
}, [paymentSettings?.subscription_status, paymentSettings?.subscription_id, paymentSettings?.xero_subscription_status, isAuthenticateForPayrolls]);

    useEffect(() => {
    dispatch(stripePricingTableListRequest());
  }, []);

  // Only fetch dashboard if we need participant count and don't have it from payment settings (e.g. from login).
  useEffect(() => {
    const hasParticipantCount =
      paymentSettings?.current_participant_count != null ||
      spDashboard?.all_participant_count != null;
    if (!hasParticipantCount) {
      dispatch(getSPDashboardRequest());
    }
  }, [dispatch, paymentSettings?.current_participant_count, spDashboard?.all_participant_count]);

  const [isFetchCurrentSubs, setIsFetchCurrentSubs] = useState(false);
  const [isCurrentSubscription, setIsCurrentSubscription] = useState(null);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    unSubscribeChannels();
    dispatch(callLogout());
    navigate("/");
  };

  useEffect(() => {
    getCurrentSubscription();
  }, []);

  const getCurrentSubscription = async () => {
    try {
      setIsFetchCurrentSubs(true);
      const response = await api.stripeCurrentSubscription();
      setIsFetchCurrentSubs(false);
      if (!response) return;

      const sub = response.subscription;
      const paymentSetting = paymentSettings || {};

      // Xero path: current_plan is a JSON string with name, subscriptionItems, etc.
      if (
        sub?.current_plan &&
        paymentSetting?.xero_subscription_status !== "CANCELLED" &&
        paymentSetting?.xero_subscription_status !== "PAST_DUE"
      ) {
        try {
          const resData = JSON.parse(sub.current_plan);
          const noOfParticipants = resData?.name?.split(" ");
          const priceAmount =
            resData?.subscriptionItems?.length > 0
              ? resData.subscriptionItems[0]?.price?.amount
              : "N/A";
          setIsCurrentSubscription({
            ...sub,
            noOfParticipants: noOfParticipants?.[2]
              ? `${noOfParticipants[2]} Participants`
              : "N/A",
            duration: `${priceAmount} Plus GST/month`,
            subscriptionPlan: noOfParticipants?.[0] && noOfParticipants?.[1]
              ? `${noOfParticipants[0]} ${noOfParticipants[1]}`
              : "Current plan",
            billingInterval: "Monthly",
          });
        } catch (_) {
          setIsCurrentSubscription(null);
        }
        return;
      }

      // Stripe path: plan.nickname and metadata
      if (sub?.plan?.nickname) {
        const [participantsRange, pricingDetails] =
          sub.plan.nickname.split("$");
        const interval =
          sub?.items?.data?.[0]?.plan?.recurring?.interval ||
          sub?.plan?.recurring?.interval;
        const billingInterval =
          interval === "year" ? "Annually" : "Monthly";
        setIsCurrentSubscription({
          ...sub.plan,
          noOfParticipants: (participantsRange || "").trim(),
          duration: (pricingDetails || "").trim(),
          subscriptionPlan: sub?.metadata?.product_name || "Current plan",
          billingInterval,
        });
      }
    } catch (err) {
      setIsFetchCurrentSubs(false);
    }
  };

  // console.log("pricingList", pricingList);

  return (
    <div className='min-h-full'>
      {istrailExpired === false && <SideBarNew />}
      <main>
        <div
          className={classNames(
            istrailExpired === false
              ? sideBarPadding
                ? "lg:pl-[4rem] duration-300"
                : "lg:pl-64 duration-300"
              : "",
            `${sideBarPadding} flex flex-col fullScreenHeight`
          )}
        >
          {istrailExpired === false ? (
            <Header title='Subscription Plans' cssClass={"z-[9]"} />
          ) : (
            <header className='bg-white flex items-center sticky top-0 z-[10] border-b border-gray-200 sm:px-6 lg:px-8 py-[20px] h-[80px]'>
              <div className='items-center w-full flex sm:flex sm:items-center sm:justify-between'>
                <div className='flex-1 min-w-0'>
                  <h1 className='text-xl font-semi bold text-gray-900 capitalize'>
                    Subscription Plans
                  </h1>
                </div>
                <div className='mt-4 flex justify-space-between sm:mt-0 space-x-2 print:hidden'>
                  <div className='md:mt-0 md:ml-auto space-x-2 md:space-x-4 hidden sm:flex'>
                    <CustomButton
                      clickHandler={handleLogout}
                      label={"Logout"}
                      Icon={<BiExit />}
                      variant={"danger"}
                    />
                  </div>
                </div>
              </div>
            </header>
          )}
          {/* <stripe-pricing-table
                    pricing-table-id='prctbl_1OUhxTKaVQE0Yj31iQG5kzOv'
                    publishable-key='pk_live_51OUh3DKaVQE0Yj313K7isXPQT8ezdLAX5NtaZdImduttVKADKoobqwgtpCOanchlzHsMjOV6ZaJ5PB8W9dDDKg2500QecN0mvg'
                  ></stripe-pricing-table> */}

          {pricingListLoader ? (
            <CircularLoader
              classes='flex justify-center items-center'
              classloader='loader-l'
            />
          ) : (
            <>
              {/* Pricing Table */}
              {/*  <div>
                  <PricingSection
                    isCurrentSubscription={isCurrentSubscription}
                    paymentSettings={paymentSettings}
                    data={pricingList}
                  />
                </div> */}

              {/* New Pricing Table */}

              <PricingTableNew
                isCurrentSubscription={isCurrentSubscription}
                paymentSettings={paymentSettings}
                data={pricingList}
                isTrialChoosePlanBtnClick={isTrialChoosePlanBtnClick}
                isShowXeroButton={isShowXeroButton}
                currentParticipantCount={
                  paymentSettings?.current_participant_count ??
                  spDashboard?.all_participant_count ??
                  0
                }
              />

              {/* Subscription checkout testing */}
              {/* <SubscriptionCheckout /> */}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default PricingTable;
